/**
 * MemoryViewBinding — deterministic, engine-free ViewBinding used by the
 * conformance host (`scripts/native-bridge-host-sim.ts`), the JS smoke
 * suite, and cross-language E2E tests. Implements the MNBP/1 catalog
 * semantics (camera state, layer/marker registries, draw sessions) without
 * any rendering engine, so native SDK behavior can be asserted headlessly.
 */
import {
  BridgeMethodError, ERR_METHOD_NOT_FOUND, ERR_INVALID_PARAMS, ERR_ENGINE, ERR_UNSUPPORTED,
} from './protocol';
import type { ViewBinding } from './BridgeHost';

const TINY_PNG_DATAURL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

function req<T>(v: T | undefined | null, name: string): T {
  if (v === undefined || v === null) throw new BridgeMethodError(ERR_INVALID_PARAMS, `missing param '${name}'`);
  return v;
}

export class MemoryViewBinding implements ViewBinding {
  readonly kind: '2d' | '3d';

  camera2d = { center: [0, 20] as [number, number], zoom: 2, rotation: 0, pitch: 0 };
  camera3d = { position: [77.2, 28.6, 15000] as [number, number, number], heading: 0, tilt: -90, roll: 0 };
  layers = new Map<string, { type: string; visible: boolean; opacity: number; data?: any }>();
  markers = new Map<string, { lngLat: [number, number]; color: string; label?: string | null }>();
  draw: { activeType: string | null; features: any[] } = { activeType: null, features: [] };
  calls: Array<{ method: string; params: Record<string, any> }> = [];

  private emits = new Map<string, (data: unknown) => void>();
  private markerSeq = 0;

  constructor(kind: '2d' | '3d', public options: Record<string, any> = {}) {
    this.kind = kind;
    if (kind === '2d') {
      if (Array.isArray(options.center)) this.camera2d.center = options.center as [number, number];
      if (typeof options.zoom === 'number') this.camera2d.zoom = options.zoom;
      if (typeof options.rotation === 'number') this.camera2d.rotation = options.rotation;
      if (typeof options.pitch === 'number') this.camera2d.pitch = options.pitch;
    } else if (options.camera) {
      this.camera3d = { ...this.camera3d, ...options.camera };
    }
  }

  /** Test hook — fire a subscribed event by type. Returns true if delivered. */
  trigger(type: string, data: unknown): boolean {
    const emit = this.emits.get(type);
    if (!emit) return false;
    emit(data);
    return true;
  }

  call(method: string, p: Record<string, any>): unknown {
    this.calls.push({ method, params: p });
    if (this.kind === '2d') return this.call2d(method, p);
    return this.call3d(method, p);
  }

  // ── 2D semantics ──
  private call2d(method: string, p: Record<string, any>): unknown {
    switch (method) {
      case 'map.setCenter': this.camera2d.center = req(p.center, 'center'); return {};
      case 'map.getCenter': return { center: this.camera2d.center };
      case 'map.setZoom': this.camera2d.zoom = req(p.zoom, 'zoom'); return {};
      case 'map.getZoom': return { zoom: this.camera2d.zoom };
      case 'map.setRotation': this.camera2d.rotation = req(p.rotation, 'rotation'); return {};
      case 'map.getRotation': return { rotation: this.camera2d.rotation };
      case 'map.setPitch': this.camera2d.pitch = req(p.pitch, 'pitch'); return {};
      case 'map.getPitch': return { pitch: this.camera2d.pitch };
      case 'map.getCamera': return { ...this.camera2d };
      case 'map.flyTo': case 'map.easeTo': case 'map.jumpTo': {
        if (p.center) this.camera2d.center = p.center;
        if (typeof p.zoom === 'number') this.camera2d.zoom = p.zoom;
        if (typeof p.bearing === 'number') this.camera2d.rotation = p.bearing;
        if (typeof p.pitch === 'number') this.camera2d.pitch = p.pitch;
        // Camera settles asynchronously in a real engine; emit moveend if subscribed.
        queueMicrotask(() => this.trigger('moveend', { camera: { ...this.camera2d } }));
        return {};
      }
      case 'map.panBy': return {};
      case 'map.fitExtent': {
        const e = req<number[]>(p.extent, 'extent');
        this.camera2d.center = [(e[0] + e[2]) / 2, (e[1] + e[3]) / 2];
        return {};
      }
      case 'map.getExtent': {
        const [cx, cy] = this.camera2d.center;
        const span = 360 / Math.pow(2, this.camera2d.zoom);
        return { extent: [cx - span / 2, cy - span / 4, cx + span / 2, cy + span / 4] };
      }
      case 'map.stop': case 'map.updateSize': case 'map.setStyle': case 'map.setProjection':
      case 'map.showGISToolBar': case 'map.hideGISToolBar': return {};
      case 'map.queryRenderedFeatures': case 'map.hitTest':
        return { features: { type: 'FeatureCollection', features: [] } };
      case 'map.toMapCoordinate': {
        const [x, y] = req<[number, number]>(p.pixel, 'pixel');
        return { coord: [this.camera2d.center[0] + (x - 400) / 100, this.camera2d.center[1] - (y - 300) / 100] };
      }
      case 'map.toPixelCoordinate': {
        const [lon, lat] = req<[number, number]>(p.coord, 'coord');
        return { pixel: [400 + (lon - this.camera2d.center[0]) * 100, 300 - (lat - this.camera2d.center[1]) * 100] };
      }
      case 'bridge.screenshot': return { dataUrl: TINY_PNG_DATAURL };
      default:
        if (method.startsWith('layers.')) return this.callLayers(method, p);
        if (method.startsWith('markers.')) return this.callMarkers(method, p);
        if (method.startsWith('draw.')) return this.callDraw(method, p);
        throw new BridgeMethodError(ERR_METHOD_NOT_FOUND, `unknown method '${method}'`);
    }
  }

  // ── 3D semantics ──
  private call3d(method: string, p: Record<string, any>): unknown {
    switch (method) {
      case 'scene.setCamera': this.camera3d = { ...this.camera3d, ...req(p.camera, 'camera') }; return {};
      case 'scene.getCamera': return { camera: { ...this.camera3d } };
      case 'scene.flyTo': {
        this.camera3d = { ...this.camera3d, ...req(p.camera, 'camera') };
        queueMicrotask(() => this.trigger('camera-change', { camera: { ...this.camera3d } }));
        return {};
      }
      case 'scene.lookAt': return {};
      case 'scene.setTerrain': case 'scene.setSceneMode': case 'scene.setNavigationMode':
      case 'scene.setLightingTime': case 'scene.setGlobeShow':
      case 'scene.showGISToolBar': case 'scene.hideGISToolBar': return {};
      case 'scene.getElevation': return { elevation: 216 };
      case 'scene.screenToGeo': {
        const pos = this.camera3d.position;
        return { coord: [pos[0], pos[1], 0] };
      }
      case 'scene.geoToScreen': return { pixel: [400, 300] };
      case 'layers.addTileset3D': {
        const id = String(req(p.id, 'id'));
        if (this.layers.has(id)) throw new BridgeMethodError(ERR_ENGINE, `layer '${id}' already exists`);
        this.layers.set(id, { type: 'tileset3d', visible: true, opacity: 1 });
        return { id };
      }
      case 'layers.setVisible': this.requireLayer(p).visible = !!p.visible; return {};
      case 'layers.setOpacity': this.requireLayer(p).opacity = Number(req(p.opacity, 'opacity')); return {};
      case 'layers.remove': this.requireLayer(p); this.layers.delete(String(p.id)); return {};
      case 'layers.clear': this.layers.clear(); return {};
      case 'layers.list': return this.listLayers();
      case 'bridge.screenshot':
        throw new BridgeMethodError(ERR_UNSUPPORTED, 'screenshot supports 2d views in v1');
      default:
        if (method.startsWith('markers.') || method.startsWith('draw.') || method.startsWith('layers.')) {
          throw new BridgeMethodError(ERR_UNSUPPORTED, `${method} is not supported on 3d views in v1`);
        }
        throw new BridgeMethodError(ERR_METHOD_NOT_FOUND, `unknown method '${method}'`);
    }
  }

  // ── shared registries ──
  private callLayers(method: string, p: Record<string, any>): unknown {
    switch (method) {
      case 'layers.addGeoJson': {
        const id = String(req(p.id, 'id'));
        if (this.layers.has(id)) throw new BridgeMethodError(ERR_ENGINE, `layer '${id}' already exists`);
        this.layers.set(id, { type: 'geojson', visible: true, opacity: 1, data: req(p.data, 'data') });
        return { id };
      }
      case 'layers.addTile': {
        const id = String(req(p.id, 'id'));
        if (this.layers.has(id)) throw new BridgeMethodError(ERR_ENGINE, `layer '${id}' already exists`);
        const kind = String(req(p.kind, 'kind'));
        if (!['osm', 'xyz', 'wms', 'mvt', 'dem'].includes(kind)) {
          throw new BridgeMethodError(ERR_INVALID_PARAMS, `unknown tile kind '${kind}'`);
        }
        if (kind !== 'osm') req(p.url, 'url');
        this.layers.set(id, { type: kind, visible: true, opacity: typeof p.opacity === 'number' ? p.opacity : 1 });
        return { id };
      }
      case 'layers.addTileset3D':
        throw new BridgeMethodError(-32002, 'layers.addTileset3D requires a 3d view');
      case 'layers.setData': this.requireLayer(p).data = req(p.data, 'data'); return {};
      case 'layers.setVisible': this.requireLayer(p).visible = !!p.visible; return {};
      case 'layers.setOpacity': this.requireLayer(p).opacity = Number(req(p.opacity, 'opacity')); return {};
      case 'layers.remove': this.requireLayer(p); this.layers.delete(String(p.id)); return {};
      case 'layers.clear': this.layers.clear(); return {};
      case 'layers.list': return this.listLayers();
      default:
        throw new BridgeMethodError(ERR_METHOD_NOT_FOUND, `unknown method '${method}'`);
    }
  }

  private listLayers() {
    return { layers: Array.from(this.layers.entries()).map(([id, l]) => ({ id, type: l.type, visible: l.visible, opacity: l.opacity })) };
  }

  private requireLayer(p: Record<string, any>) {
    const id = String(req(p.id, 'id'));
    const entry = this.layers.get(id);
    if (!entry) throw new BridgeMethodError(ERR_ENGINE, `unknown layer '${id}'`);
    return entry;
  }

  private callMarkers(method: string, p: Record<string, any>): unknown {
    switch (method) {
      case 'markers.add': {
        const id = p.id ? String(p.id) : `m${++this.markerSeq}`;
        if (this.markers.has(id)) throw new BridgeMethodError(ERR_ENGINE, `marker '${id}' already exists`);
        this.markers.set(id, { lngLat: req(p.lngLat, 'lngLat'), color: p.color ?? '#e11d48', label: p.label ?? null });
        return { id };
      }
      case 'markers.update': {
        const rec = this.markers.get(String(req(p.id, 'id')));
        if (!rec) throw new BridgeMethodError(ERR_ENGINE, `unknown marker '${p.id}'`);
        if (p.lngLat) rec.lngLat = p.lngLat;
        if (typeof p.color === 'string') rec.color = p.color;
        if (p.label !== undefined) rec.label = p.label;
        return {};
      }
      case 'markers.remove': {
        if (!this.markers.delete(String(req(p.id, 'id')))) {
          throw new BridgeMethodError(ERR_ENGINE, `unknown marker '${p.id}'`);
        }
        return {};
      }
      case 'markers.clear': this.markers.clear(); return {};
      default:
        throw new BridgeMethodError(ERR_METHOD_NOT_FOUND, `unknown method '${method}'`);
    }
  }

  private callDraw(method: string, p: Record<string, any>): unknown {
    switch (method) {
      case 'draw.start': {
        const type = String(req(p.type, 'type'));
        if (!['point', 'line', 'polygon'].includes(type)) {
          throw new BridgeMethodError(ERR_INVALID_PARAMS, 'draw type must be point|line|polygon');
        }
        this.draw.activeType = type;
        return {};
      }
      case 'draw.stop': case 'draw.abort': this.draw.activeType = null; return {};
      case 'draw.finish': {
        if (!this.draw.activeType) return { feature: null };
        const feature = {
          type: 'Feature',
          geometry: this.draw.activeType === 'point'
            ? { type: 'Point', coordinates: this.camera2d.center }
            : this.draw.activeType === 'line'
              ? { type: 'LineString', coordinates: [this.camera2d.center, [this.camera2d.center[0] + 1, this.camera2d.center[1]]] }
              : { type: 'Polygon', coordinates: [[this.camera2d.center, [this.camera2d.center[0] + 1, this.camera2d.center[1]], [this.camera2d.center[0], this.camera2d.center[1] + 1], this.camera2d.center]] },
          properties: {},
        };
        this.draw.features.push(feature);
        this.draw.activeType = null;
        queueMicrotask(() => this.trigger('draw.create', { feature }));
        return { feature };
      }
      case 'draw.removeLastPoint': return {};
      case 'draw.getFeatures': return { features: { type: 'FeatureCollection', features: [...this.draw.features] } };
      case 'draw.clear': this.draw.features = []; return {};
      default:
        throw new BridgeMethodError(ERR_METHOD_NOT_FOUND, `unknown method '${method}'`);
    }
  }

  subscribe(type: string, emit: (data: unknown) => void): (() => void) | null {
    const known2d = ['click', 'dblclick', 'contextmenu', 'pointermove', 'movestart', 'moveend',
      'zoomchange', 'rotationchange', 'load', 'error', 'draw.point', 'draw.create', 'draw.abort', 'marker.click'];
    const known3d = ['ready', 'click', 'camera-change', 'error'];
    const known = this.kind === '2d' ? known2d : known3d;
    if (!known.includes(type)) return null;
    this.emits.set(type, emit);
    if (type === 'load' || type === 'ready') queueMicrotask(() => emit({}));
    return () => this.emits.delete(type);
  }

  destroy(): void {
    this.emits.clear();
    this.layers.clear();
    this.markers.clear();
  }
}
